<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import AiAgentForm from './AiAgentForm.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const uiFlags = useMapGetter('aiAgents/getUIFlags');
const currentAgent = ref(null);
const isLoading = ref(true);

const agentId = computed(() => Number(route.params.id));

const goToList = () => {
  router.push({ name: 'ai_agents' });
};

const goToRuns = () => {
  router.push({ name: 'ai_agents_runs', params: { id: agentId.value } });
};

const updateAgent = async payload => {
  const updatedAgent = await store.dispatch('aiAgents/update', {
    id: agentId.value,
    data: payload,
  });
  if (!updatedAgent) return null;

  useAlert(t('AI_AGENTS.API.UPDATE_SUCCESS'));
  return updatedAgent;
};

const save = async payload => {
  const updatedAgent = await updateAgent(payload);
  if (!updatedAgent) return;

  currentAgent.value = await store.dispatch('aiAgents/show', agentId.value);
};

const publish = async payload => {
  const updatedAgent = await updateAgent(payload);
  if (!updatedAgent) return;

  await store.dispatch('aiAgents/publish', agentId.value);
  useAlert(t('AI_AGENTS.API.PUBLISH_SUCCESS'));
  goToList();
};

const loadAgent = async () => {
  try {
    isLoading.value = true;
    currentAgent.value = await store.dispatch('aiAgents/show', agentId.value);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  loadAgent();
});
</script>

<template>
  <div
    v-if="isLoading"
    class="flex flex-1 items-center justify-center p-8 text-sm text-n-slate-11"
  >
    {{ t('AI_AGENTS.LIST.LOADING') }}
  </div>
  <AiAgentForm
    v-else-if="currentAgent"
    :key="currentAgent.id"
    :agent="currentAgent"
    :title="t('AI_AGENTS.FORM.EDIT_TITLE')"
    :is-saving="uiFlags.isUpdating"
    :is-publishing="uiFlags.isUpdating || uiFlags.isPublishing"
    show-runs-link
    @save="save"
    @publish="publish"
    @cancel="goToList"
    @view-runs="goToRuns"
  />
</template>
